import { ApiEndpoints } from "@shared/api/api-endpoints";
import {
  communicationApiClient,
  mxApiClient,
  onboardingApiClient,
} from "@shared/api/clients/apiClientFactory";
import { QueryStringParameters } from "@shared/api/url-builder";
import { DEFAULT_PAGE_NUMBER, MODAL_PAGE_SIZE } from "@shared/utils/constants";
import { getRemoteUtilityId } from "@shared/utils/getUtilityId";

export const getAllRoutes = async (
  page: any,
  utilityId: string,
  search_data?: string,
  readType?: any,
  area?: any,
) => {
  const url = ApiEndpoints.createUrlWithQueryParameters(
    "mx",
    "route",
    (query) => {
      query.push("remote_utility_id", utilityId);
      query.push("page", page);
      query.push("is_consumer_count", "1");

      if (readType && readType.toLowerCase() !== "all") {
        query.push("read_type", readType.toUpperCase());
      }

      if (area && area.toUpperCase() !== "ALL") {
        query.push("area", area.toUpperCase());
      }
      if (search_data) {
        query.push("search_data", search_data);
      }
    },
  );
  const response = await mxApiClient.get(url);
  return response.data ?? [];
};

export const exportAllRoutes = async (
  page: any,
  utilityId: string,
  search_data?: string,
  readType?: any,
  area?: any,
): Promise<any> => {
  const url = ApiEndpoints.createUrlWithQueryParameters(
    "mx",
    "route",
    (query) => {
      query.push("remote_utility_id", utilityId);
      if (page) query.push("page", page);
      query.push("is_consumer_count", "1");

      if (readType && readType.toLowerCase() !== "all") {
        query.push("read_type", readType.toUpperCase());
      }

      if (area && area.toUpperCase() !== "ALL") {
        query.push("area", area.toUpperCase());
      }

      if (search_data) {
        query.push("search_data", search_data);
      }

      query.push("export_format", "csv");
    },
  );

  const response = await mxApiClient.get(url);

  return response.data;
};

export const getCategory = async (utilityId: string) => {
  const url = ApiEndpoints.createUrlWithQueryParameters(
    "onboarding",
    "config-map/category_json",
    (query) => {
      query.push("remote_utility_id", utilityId);
      query.push("config_level", "category");
    },
  );
  const response = await onboardingApiClient.get(url);
  return response.data.result ?? [];
};

export const getSubCategory = async (utilityId: string, code_list: any[]) => {
  const url = ApiEndpoints.createUrlWithQueryParameters(
    "onboarding",
    "config-map/category_json",
    (query) => {
      query.push("remote_utility_id", utilityId);
      query.push("config_level", "sub_category");
      for (let code of code_list) {
        query.push("code_list", code);
      }
    },
  );

  const response = await onboardingApiClient.get(url);
  return response.data.result ?? [];
};

export const getMeterSummaryDashboardByPremises = async (
  utilityId: string,
  premises?: any[],
  category?: any[],
  subCategory?: any[],
  utilityService?: any[],
  meterType?: string,
) => {
  const url = ApiEndpoints.createUrlWithQueryParameters(
    "mx",
    `meter-cycle/dashboard`,
    (query) => {
      if (premises) {
        for (const item of premises) {
          query.push("premises", item);
        }
      }
      if (category) {
        for (const item of category) {
          query.push("category", item);
        }
      }
      if (subCategory) {
        for (const item of subCategory) {
          query.push("sub_category", item);
        }
      }
      if (utilityService) {
        for (const item of utilityService) {
          query.push("utility_service", item);
        }
      }
      if (meterType) {
        query.push("meter_type", meterType);
      }
      query.push("remote_utility_id", utilityId);
    },
  );
  const response = await mxApiClient.get(url);
  return response.data.result ?? [];
};
export const getPremises = async (utilityId: string, code_list: any[]) => {
  const url = ApiEndpoints.createUrlWithQueryParameters(
    "onboarding",
    "config-map/territory_json",
    (query) => {
      query.push("remote_utility_id", utilityId);
      query.push("config_level", "premise");
      for (let code of code_list) {
        query.push("code_list", code);
      }
    },
  );

  const response = await onboardingApiClient.get(url);
  return response.data.result ?? [];
};

export const getMeterSummaryDashboard = async (
  route_id: any,
  utilityId: string,
) => {
  const url = ApiEndpoints.createUrlWithQueryParameters(
    "mx",
    `meter-cycle/dashboard`,
    (query) => {
      query.push("route_id", route_id);
      query.push("remote_utility_id", utilityId);
    },
  );
  const response = await mxApiClient.get(url);
  return response.data.result ?? [];
};

export const getRouteData = async (route_id: any, utilityId: string) => {
  const url = ApiEndpoints.createUrlWithQueryParameters(
    "mx",
    `route/details/${route_id}`,
    (query) => {
      query.push("remote_utility_id", utilityId);
    },
  );
  const response = await mxApiClient.get(url);
  return response.data.result ?? [];
};

export const getMetersCount = async (
  route_id: any,
  page: any,
  utilityId: string,
  meter_type: any,
  filter_status: any,
  reading_status: any,
  search_data?: any,
) => {
  const url = ApiEndpoints.createUrlWithQueryParameters(
    "mx",
    `meter-count`,
    (query) => {
      query.push("route_id", route_id);
      query.push("remote_utility_id", utilityId);
      query.push("page", page);
      query.push("is_consumer_info", "1");

      if (meter_type && meter_type !== "all") {
        query.push("meter_type", meter_type);
      }
      if (filter_status && filter_status !== "all") {
        query.push("filter_status", filter_status);
      }
      if (reading_status && reading_status !== "all") {
        query.push("reading_status", reading_status);
      }
      if (search_data) {
        query.push("search_data", search_data);
      }
    },
  );
  const response = await mxApiClient.get(url);
  return response.data ?? [];
};
export const getPerformanceDashboard = async (
  route_id: any,
  utilityId: string,
) => {
  const url = ApiEndpoints.createUrlWithQueryParameters(
    "mx",
    `meter-route/performance/${route_id}`,
    (query) => {
      query.push("remote_utility_id", utilityId);
    },
  );
  const response = await mxApiClient.get(url);
  return response.data.result ?? [];
};

export const createRoute = async (data: any) => {
  const payload = {
    route_name: data.route_name,
    utility_service: data.utility_service,
    meter_type: data.meter_type,
    category: data.category,
    sub_category: data.sub_category,
    area: data.area,
    sub_area: data.sub_area,
    premise: data.premise,
    remote_utility_id: data.remote_utility_id,
  };
  const url = ApiEndpoints.createUrlWithQueryParameters("mx", `route`);
  const response = await mxApiClient.post(url, payload);
  return response.data.result ?? [];
};

export const updateRoute = async (routeId: any, data: any) => {
  const payload = {
    route_name: data.route_name,
    utility_service: data.utility_service,
    meter_type: data.meter_type,
    category: data.category,
    sub_category: data.sub_category,
    area: data.area,
    sub_area: data.sub_area,
    premise: data.premise,
    remote_utility_id: data.remote_utility_id,
  };
  const url = ApiEndpoints.createUrlWithQueryParameters(
    "mx",
    `route/${routeId}`,
  );
  const response = await mxApiClient.put(url, payload);
  return response.data.result ?? [];
};

export const routeAuditTrail = async (
  route_id: any,
  utilityId: string,
  actionType: string,
) => {
  const url = ApiEndpoints.createUrlWithQueryParameters(
    "activityLog",
    `logs`,
    (query) => {
      query.push("patient_id", route_id);
      query.push("remote_utility_id", utilityId);
      query.push("action_type", actionType);
      query.push("module", "mx");
    },
  );
  const response = await communicationApiClient.get(url);
  return response.data ?? [];
};

export const api = {
  routes: {
    get: {
      list: getAllRoutes,
      exportAllRoutes: exportAllRoutes,
      category: getCategory,
      subCategory: getSubCategory,
      premises: getPremises,
      routeData: getRouteData,
      metersCount: getMetersCount,
      performanceDashboard: getPerformanceDashboard,
      auditTrail: routeAuditTrail,
    },
    create: {
      DashboardByPremises: getMeterSummaryDashboardByPremises,
      Dashboard: getMeterSummaryDashboard,
      createRoute: createRoute,
      updateRoute: updateRoute,
    },
  },
};
export const routeApi = {
  getMeterCycle: async (routeId: any, searchData?: string): Promise<any> => {
    const url = ApiEndpoints.createUrlWithQueryParameters(
      "mx",
      "meter-cycle",
      (qs) => {
        qs.push("remote_utility_id", getRemoteUtilityId());
        qs.push("is_consumer_count", true);
        qs.push("route_id", routeId);

        // Only add search_data if it exists and is not empty
        if (searchData && searchData.trim()) {
          qs.push("search_data", searchData.trim());
        }
      },
    );
    const response = await mxApiClient.get(url);
    return response.data;
  },
  // getRoutePremise: async (
  //   code_name: string | string[], // Accept both single string or array
  //   searchData?: string,
  //   page: number = 1,
  //   limit: number = 10
  // ): Promise<any> => {
  //   const url = ApiEndpoints.createUrlWithQueryParameters(
  //     "onboarding",
  //     "config-map/territory_json",
  //     (qs) => {
  //       qs.push("remote_utility_id", getRemoteUtilityId());

  //       // Handle multiple code_names - push each separately
  //       const codeNames = Array.isArray(code_name) ? code_name : [code_name];
  //       codeNames.forEach((code) => {
  //         if (code) {
  //           qs.push("code_name", code);
  //         }
  //       });

  //       // Add pagination parameters
  //       qs.push("page", page.toString());
  //       qs.push("limit", limit.toString());
  //       qs.push("is_pagination_required", "true");

  //       // Only add search_data if it exists and is not empty
  //       if (searchData && searchData.trim()) {
  //         qs.push("search_data", searchData.trim());
  //       }
  //     }
  //   );
  //   const response = await onboardingApiClient.get(url);
  //   return response.data;
  // },

  getRoutePremise: async (
    code_name: string | string[],
    searchData?: string,
    page: number = 1,
    limit: number = 10,
    area?: string, // Add this
    subArea?: string, // Add this
  ): Promise<any> => {
    const REMOTE_UTILITY_ID = getRemoteUtilityId();
    const isSearch = !!searchData && searchData.trim() !== "";

    const url = ApiEndpoints.createUrlWithQueryParameters(
      "onboarding",
      isSearch ? "search/onboarding_config" : "config-map/territory_json",
      (qs) => {
        // Common pagination params
        qs.push("remote_utility_id", REMOTE_UTILITY_ID);
        qs.push("limit", limit.toString());
        qs.push("is_pagination_required", true);
        qs.push("page", page.toString());

        // Normalize code_name into array
        const codeNames = Array.isArray(code_name) ? code_name : [code_name];

        // Always pass code_name in BOTH APIs
        codeNames.forEach((code) => {
          if (code) qs.push("code_name", code);
        });

        if (isSearch) {
          // Search-specific params
          qs.push("search_field", "territory_json-premise");
          qs.push("search_data", searchData!.trim());
        }

        // Add area and subArea filters
        if (area && area !== "all") {
          qs.push("area", area); // or "area_code" - check your backend
        }

        if (subArea && subArea !== "all") {
          qs.push("code_list", subArea); // or "sub_area_code" - check your backend
        }
      },
    );

    const response = await onboardingApiClient.get(url);
    return response.data;
  },

  updateReadCycle: async (payload: any): Promise<any> => {
    const url = ApiEndpoints.createUrl("mx", "meter-cycle/update-route");
    const response = await mxApiClient.put(url, payload);
    return response.data;
  },

  //https://api-mx-dev.bynry.com/api/mx/route/
  addRoute: async (payload: any): Promise<any> => {
    const url = ApiEndpoints.createUrl("mx", "route");
    const response = await mxApiClient.post(url, payload);
    return response.data;
  },

  addPremises: async (
    payload: any,
    routeId: any,
    newRouteId?: any,
  ): Promise<any> => {
    const url = ApiEndpoints.createUrlWithQueryParameters(
      "mx",
      "route/update-premises",
      (qs) => {
        qs.push("remote_utility_id", getRemoteUtilityId());
        qs.push("route_id", routeId);
        qs.push("new_route_id", newRouteId);
      },
    );
    const response = await mxApiClient.put(url, payload);
    return response.data;
  },
  addPremisesModal: async (payload: any, routeId: any): Promise<any> => {
    const url = ApiEndpoints.createUrlWithQueryParameters(
      "mx",
      "route/update-premises",
      (qs) => {
        qs.push("remote_utility_id", getRemoteUtilityId());
        qs.push("route_id", routeId);
      },
    );
    const response = await mxApiClient.put(url, payload);
    return response.data;
  },

  getRouteSummary: async (): Promise<any> => {
    const url = ApiEndpoints.createUrlWithQueryParameters(
      "mx",
      "route/summary",
      (qs) => {
        qs.push("remote_utility_id", getRemoteUtilityId());
      },
    );
    const response = await mxApiClient.get(url);
    return response.data;
  },
  getPremise: async (
    page: number = 1,
    limit: number = MODAL_PAGE_SIZE,
    area?: string,
    sub_area?: string,
    search_data?: string,
  ): Promise<any> => {
    const REMOTE_UTILITY_ID = getRemoteUtilityId();
    const isSearch = !!search_data && search_data.trim() !== "";

    const url = ApiEndpoints.createUrlWithQueryParameters(
      "onboarding",
      isSearch ? "search/onboarding_config" : "config-map/territory_json",
      (qs) => {
        // Common pagination params
        qs.push("remote_utility_id", REMOTE_UTILITY_ID);
        qs.push("limit", limit.toString());
        qs.push("is_pagination_required", true);
        qs.push("page", page.toString());

        if (isSearch) {
          // Search behavior
          qs.push("search_field", "territory_json-premise"); // SAME pattern as your getRoutePremise
          qs.push("search_data", search_data!.trim());
        } else {
          // Normal behavior
          qs.push("config_level", "premise");

          if (area && area !== "all") {
            qs.push("area", area);
          }

          if (sub_area && sub_area !== "all") {
            qs.push("code_list", sub_area);
          }

          if (search_data && search_data.trim() !== "") {
            qs.push("search_data", search_data.trim());
          }
        }
      },
    );

    const response = await onboardingApiClient.get(url);
    return response.data;
  },

  getPremiseModal: async (
    page?: number,
    area?: string,
    sub_area?: string,
    search_data?: string,
    exclude_code_name?: string,
  ): Promise<any> => {
    const REMOTE_UTILITY_ID = getRemoteUtilityId();
    const PAGE_SIZE = 10;

    const isSearch = !!search_data && search_data.trim() !== "";

    const endpoint = isSearch
      ? "search/onboarding_config"
      : "config-map/territory_json";

    const url = ApiEndpoints.createUrlWithQueryParameters(
      "onboarding",
      endpoint,
      (qs: QueryStringParameters) => {
        // Common params
        qs.push("remote_utility_id", REMOTE_UTILITY_ID);
        qs.push("config_level", "premise");
        qs.push("limit", PAGE_SIZE);
        qs.push("is_pagination_required", true);
        qs.push("page", page || 1);

        // Optional filters
        if (area && area !== "all") qs.push("area", area);
        if (sub_area && sub_area !== "all") qs.push("code_list", sub_area);

        // ✅ ALWAYS PASS exclude_code_name (BOTH APIs)
        if (exclude_code_name && exclude_code_name !== "all") {
          qs.push("exclude_code_name", exclude_code_name);
        }

        // Search-specific params
        if (isSearch) {
          qs.push("search_field", "territory_json-premise");
          qs.push("search_data", search_data!.trim());
        }
      },
    );

    try {
      const response = await onboardingApiClient.get(url);
      return response.data;
    } catch (error) {
      console.error("Error fetching premise modal data:", error);
      throw error;
    }
  },

  getMeters: async (params: {
    premise: string | string[];
    page?: number;
    limit?: number;
  }): Promise<any> => {
    const url = ApiEndpoints.createUrlWithQueryParameters(
      "mx",
      "meter",
      (qs) => {
        qs.push("page", params.page?.toString() || DEFAULT_PAGE_NUMBER);
        qs.push("limit", params.limit?.toString() || MODAL_PAGE_SIZE);
        qs.push("remote_utility_id", getRemoteUtilityId());

        // Handle multiple premises - push each separately
        const premises = Array.isArray(params.premise)
          ? params.premise
          : [params.premise];
        premises.forEach((premiseCode) => {
          if (premiseCode) {
            qs.push("premise", premiseCode);
          }
        });
      },
    );
    const response = await mxApiClient.get(url);
    return response.data;
  },

  getRouteDashboard: async (routeId: any): Promise<any> => {
    const url = ApiEndpoints.createUrlWithQueryParameters(
      "mx",
      "meter-route/dashboard",
      (qs) => {
        qs.push("remote_utility_id", getRemoteUtilityId());
        qs.push("route_id", routeId);
      },
    );
    const response = await mxApiClient.get(url);
    return response.data;
  },

  getMeterSummary: async (premise: any): Promise<any> => {
    const url = ApiEndpoints.createUrlWithQueryParameters(
      "mx",
      "meter-route/dashboard",
      (qs) => {
        qs.push("remote_utility_id", getRemoteUtilityId());
        for (const p of premise) {
          qs.push("premises", p);
        }
      },
    );
    const response = await mxApiClient.get(url);
    return response.data;
  },

  getPerformanceDetails: async (
    routeId: any,
    utilityId: string,
    id?: number,
  ) => {
    const url = ApiEndpoints.createUrlWithQueryParameters(
      "mx",
      `meter-route/performance/${routeId}`,
      (query) => {
        query.push("remote_utility_id", utilityId);
        if (id) {
          query.push("id", id);
        }
      },
    );
    const response = await mxApiClient.get(url);
    return response.data.result ?? [];
  },
  getReadingStatus: async (): Promise<any> => {
    const url = ApiEndpoints.createUrl(
      "mx",
      "choices/meter_meter_reading_status",
    );
    const response = await mxApiClient.get(url);
    return response.data;
  },
  getMeterType: async (): Promise<any> => {
    const url = ApiEndpoints.createUrl("mx", "choices/meter_type");
    const response = await mxApiClient.get(url);
    return response.data;
  },
  getMeterStatus: async (): Promise<any> => {
    const url = ApiEndpoints.createUrl("mx", "choices/meter_status");
    const response = await mxApiClient.get(url);
    return response.data;
  },
};
